import {createSelector} from '@reduxjs/toolkit'

import {includes} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'

import {
    ApiResult,
    APIStatus,
    PayDueType,
    PayLaterMemberStatus,
    PayLaterMemberStatusType,
    PayLaterPeriod,
    CandidateStatus,
    PayMethodType,
    PayMethod,
    ChargePointPolicy,
} from '@web/core/constants'
import {chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {orderEntryInfoSelector} from '@web/slices/common/slice'
import {orderAmountInfoSelector} from '@web/slices/orderTotal/slice'
import {payLaterSelector} from '@web/slices/payLater/slice'
import {payMethodAvailableSelector, payMethodInfoSelector} from '@web/slices/paymentMethod/slice'
import {naverPayPointSelector} from '@web/slices/pointPayment/slice'
import {rewardPointSelector} from '@web/slices/rewardPoint/slice'

import {naverPayPointInfoSelector} from './pointPayment'

/** 후불결제 사용가능여부  */
export const payLaterAvailableSelector = createSelector(
    orderEntryInfoSelector,
    payMethodAvailableSelector,
    (
        {isCheckout, isEasyBookingGroup, isSellerGroup},
        {availablePayMethodTypes, isRewardPointAvailable, isPayPointAvailable},
    ) => {
        const hasPayMethod = (payMethod: PayMethodType): boolean => availablePayMethodTypes.includes(payMethod)

        if (isCheckout || isEasyBookingGroup || isSellerGroup) {
            return isRewardPointAvailable && hasPayMethod(PayMethod.PAY_LATER)
        }
        return isPayPointAvailable && hasPayMethod(PayMethod.PAY_LATER)
    },
)

/** 후불결제 status selector */
const payLaterStatusSelector = createSelector(
    payLaterAvailableSelector,
    orderAmountInfoSelector,
    naverPayPointSelector,
    rewardPointSelector,
    chargePointSelector,
    payLaterSelector,
    payMoneyInfoSelector,
    (
        isPayLaterAvailable,
        {orderAmount},
        naverPayPoint,
        rewardPoint,
        chargePoint,
        {availableAmount, memberStatus: payLaterMemberStatus, candidateStatus},
        {isPayMoneyUser, availablePayment, chargePointPolicy},
    ) => {
        const pointAmount = isPayMoneyUser
            ? naverPayPoint.amount
            : eq(chargePointPolicy, ChargePointPolicy.PAYMONEY)
              ? chargePoint.amount + naverPayPoint.amount
              : naverPayPoint.amount
        /**
         * 가입미대상자도 사용가능결제수단에 후불결제 추가
         * @see https://oss.fin.navercorp.com/payOrder/payment/pull/4101
         */
        if (
            (!isPayLaterAvailable && !eq(payLaterMemberStatus, PayLaterMemberStatus.MAINTENANCE)) ||
            (!includes(payLaterMemberStatus, [
                PayLaterMemberStatus.NORMAL,
                PayLaterMemberStatus.SUSPENDED,
                PayLaterMemberStatus.OVERDUE,
            ]) &&
                eq(candidateStatus, CandidateStatus.NOT_CANDIDATE))
        ) {
            return PayLaterMemberStatus.NOT_AVAILABLE
        }
        /**
         * 23.12.06 수정
         * availablePayment(1.0 / 3.0)의 경우에는 충전포인트(chargePointAmount) 잔액조회실패 시 후불결제 사용불가
         * 2.0 / 4.0 / 4.1 / 4.2 의 경우에는 머니(chargePointAmount) 잔액조회실패 시 후불결제 사용가능
         * @see https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=32121045
         */
        const unableUsePoint =
            rewardPoint.status !== APIStatus.RESOLVED ||
            (!isPayMoneyUser && availablePayment && chargePoint.status !== APIStatus.RESOLVED)
        if (
            payLaterMemberStatus === PayLaterMemberStatus.NOT_AVAILABLE ||
            payLaterMemberStatus === PayLaterMemberStatus.MAINTENANCE ||
            unableUsePoint ||
            chargePoint.payMoney.apiResult !== ApiResult.SUCCESS
        ) {
            return PayLaterMemberStatus.MAINTENANCE
        }
        if (
            payLaterMemberStatus === PayLaterMemberStatus.SIGNUP ||
            payLaterMemberStatus === PayLaterMemberStatus.SUSPENDED ||
            payLaterMemberStatus === PayLaterMemberStatus.OVERDUE
        ) {
            return payLaterMemberStatus
        }
        if (availableAmount === 0) {
            return PayLaterMemberStatus.NO_BALANCE
        }
        /** 이용 가능 상태일 때 세부 상태 분류 */
        if (pointAmount >= orderAmount) {
            return PayLaterMemberStatus.NO_PAYMENT
        }
        return PayLaterMemberStatus.NORMAL
    },
)

const PayLaterDisabledList: PayLaterMemberStatusType[] = [
    PayLaterMemberStatus.NO_PAYMENT,
    PayLaterMemberStatus.OVERDUE,
    PayLaterMemberStatus.NO_BALANCE,
    PayLaterMemberStatus.SUSPENDED,
]

const PossiblePayLaterStatus: PayLaterMemberStatusType[] = [PayLaterMemberStatus.SIGNUP, PayLaterMemberStatus.NORMAL]

/** 후불결제 info selector */
export const payLaterInfoSelector = createSelector(
    payLaterAvailableSelector,
    orderAmountInfoSelector,
    naverPayPointInfoSelector,
    payLaterSelector,
    payMoneyInfoSelector,
    payMethodInfoSelector,
    payLaterStatusSelector,
    chargePointSelector,
    (
        isPayLaterAvailable,
        {orderAmount},
        {naverPayPoint},
        payLater,
        {isPayMoneyUser, chargePointPolicy},
        {selectedRechargePayMethod},
        payLaterStatus,
        {amount: chargePointAmount},
    ) => {
        const {availableAmount, enabled, usingAmount, payDue, payDueDate, userInfo, signUpStatus, memberStatus} =
            payLater
        const isTestGroupUser = userInfo.isTestGroup
        const isDisabled = !isPayLaterAvailable || PayLaterDisabledList.includes(payLaterStatus)
        const isAvailable = isPayLaterAvailable && payLaterStatus === PayLaterMemberStatus.NORMAL
        const shouldUsePayMoneyForPayLater = !isPayMoneyUser && eq(chargePointPolicy, ChargePointPolicy.PAYMONEY)
        const possibleMaxPayLaterAmount = isAvailable
            ? Math.max(
                  0,
                  Math.min(
                      orderAmount - naverPayPoint.amount - (shouldUsePayMoneyForPayLater ? chargePointAmount : 0),
                      availableAmount,
                  ),
              )
            : 0

        return {
            payLater,
            enabled,
            isNeedUpdate: (function () {
                if (enabled || usingAmount > 0) {
                    return (
                        // 이용 불가능 상태
                        isDisabled ||
                        // 후불결제는 포인트 전액 사용이 필요합니다.
                        naverPayPoint.amount > naverPayPoint.usingAmount ||
                        // 후불결제를 사용하기에 남은 금액이 없습니다.
                        orderAmount - naverPayPoint.usingAmount <= 0 ||
                        // 후불결제 사용 가능 금액보다 많은 금액을 사용하려고 합니다.
                        availableAmount < usingAmount ||
                        // 마이스 금액은 사용할 수 없습니다.
                        usingAmount < 0
                    )
                }
                return false
            })(),
            status: payLaterStatus,
            signUpStatus,
            possibleMaxPayLaterAmount,
            period: {
                /** 납부 예정 일 */
                payDue,
                /** 납부 예정 일자 */
                payDueDate,
                period: payDue ? PayLaterPeriod[payDue as PayDueType] : null,
            },
            /** 후불결제 결제 가능 여부 */
            canUsePayment:
                !isDisabled &&
                payLaterStatus === PayLaterMemberStatus.NORMAL &&
                memberStatus !== PayLaterMemberStatus.PENDING,
            /** 후불결제 비활성화 상태 여부 */
            isDisabled,
            /**
             * 후불결제 사용 시 전체 보유 포인트 소진 안내 필요 조건
             * - 보유 포인트 < 결제금액
             * - 보유 포인트를 모두 사용
             * - 후불결제 포인트 사용
             */
            shouldWarnAllPayPointUsed:
                (shouldUsePayMoneyForPayLater || naverPayPoint.amount > 0) &&
                Math.min(orderAmount, naverPayPoint.amount) <= naverPayPoint.usingAmount &&
                enabled,
            /**
             * 후불결제 전액사용 기능 사용가능 여부
             * - 후불결제 사용 가능 금액이 0보다 클때,
             * - 후불결제를 위해 입력한 금액과 가능한 후불결제 사용금액이 같지 않을때
             */
            canUseAllPayLater: availableAmount > 0 && !eq(possibleMaxPayLaterAmount, usingAmount),
            /** 후불결제 유도 가능 여부 */
            shouldPrompt:
                // 테스트 그룹 유저 N
                !isTestGroupUser &&
                // 후불결제 가입대상자 Y
                PossiblePayLaterStatus.includes(payLaterStatus) &&
                // 보유포인트 >= 결제금액 (보유포인트 모두 사용해야 후불결제 사용 가능)
                !(orderAmount < naverPayPoint.amount || possibleMaxPayLaterAmount <= 0) &&
                // 후불결제 사용 체크 Y
                !enabled &&
                // 충전결제 사용 금액 > 0 (후불결제와 충전결제 동시 사용 불가) | 페이머니유저는 둘다 가능
                !(!isPayMoneyUser && selectedRechargePayMethod),
            shouldPopupRender: !enabled && !selectedRechargePayMethod && orderAmount > naverPayPoint.amount,
        }
    },
)
